import React from 'react'
import { Button, CardProps } from '@common/material'
import { Card } from '@common/material'
import { PeopleOutlined as PeopleOutlinedIcon } from '@mui/icons-material'
import { CardContent, Divider } from '@mui/material'

import { BriefingSession } from '@core/entities/briefing'
import { useDiscardBriefingSession } from '@core/react-query/features/briefing'
import { localizedStrings } from '@core/strings'
import { ParticipantEntityType } from '@core/modules/briefings'
import { useToast } from '@core/toast'
import { Flex, Text } from '@fundamentals'
import { useModal } from '@common/GlobalModal'
import { BriefingAddParticipantsModal } from '@modules/briefings'
import ConfirmModal from '@common/modal/ConfirmModal'

type BriefingSessionCardProps = CardProps & {
  entityId: number
  entityType: ParticipantEntityType
  briefingSession: BriefingSession
}

export const BriefingSessionCard: React.FC<BriefingSessionCardProps> = ({
  entityId,
  entityType,
  briefingSession,
  sx,
  ...props
}) => {
  const toast = useToast()
  const { showModal, hideModal } = useModal()

  const { mutate: discardBriefingSession, isLoading: isDiscarding } =
    useDiscardBriefingSession()

  return (
    <Card
      sx={{ borderColor: 'primary.main', borderWidth: 2, ...sx }}
      {...props}
    >
      <CardContent>
        <Text fontSize={16} fontWeight={500} color='text.primary'>
          {localizedStrings.incompleteBriefingSession}
        </Text>

        <Divider sx={{ mb: 1.5, mt: 1 }} />

        <Flex mt={1.5} mb={1.5} alignItems='center'>
          <PeopleOutlinedIcon sx={{ color: 'grey' }} />
          <Text ml={1} fontSize={14} color='text.primary'>
            {localizedStrings.unsignedParticipant(
              briefingSession.getUnsignedParticipants().length,
            )}
          </Text>
        </Flex>

        <Text mb={2} fontSize={14} color='text.disabled'>
          {localizedStrings.editOrDiscardThisBriefingSession}
        </Text>

        <Flex gap={1}>
          <Button
            sx={{ flex: 1 }}
            variant='outlined'
            disabled={isDiscarding}
            onClick={() => {
              showModal(
                <ConfirmModal
                  title={localizedStrings.discardBriefingSession}
                  confirmText={localizedStrings.discard}
                  onConfirm={() => {
                    discardBriefingSession(
                      {
                        briefingSessionId: briefingSession.id,
                        referenceObjectId: entityId,
                        referenceObjectType: entityType,
                      },
                      {
                        onSuccess: () => {
                          toast.success(localizedStrings.briefingDiscarded)
                          hideModal()
                        },
                        onError: (error) => {
                          toast.error(
                            error.message ?? localizedStrings.genericError,
                          )
                        },
                      },
                    )
                  }}
                >
                  {localizedStrings.discardBriefingSessionConfirmation}
                </ConfirmModal>,
              )
            }}
          >
            {localizedStrings.discard}
          </Button>
          <Button
            sx={{ flex: 1 }}
            variant='contained'
            disabled={isDiscarding}
            onClick={() => {
              showModal(
                <BriefingAddParticipantsModal
                  entityId={entityId}
                  entityType={entityType}
                />,
              )
            }}
          >
            {localizedStrings.edit}
          </Button>
        </Flex>
      </CardContent>
    </Card>
  )
}
